"use client";

declare global {
  interface Window {
    clarity?: (...args: any[]) => any;
  }
}

// Waits up to 5s for the real Clarity script to replace the queue stub.
// The inline loader creates a queue function immediately; the real API
// replaces it once the script tag loads. We can tell them apart because
// the queue stub has a `.q` property, the real API doesn't.
function clarityReady(): Promise<void> {
  return new Promise((resolve) => {
    if (typeof window === "undefined") {
      resolve();
      return;
    }
    const deadline = Date.now() + 5000;
    const check = () => {
      const cl = window.clarity;
      if (cl && !(cl as any).q) {
        resolve();
        return;
      }
      if (Date.now() >= deadline) {
        resolve(); // Give up quietly — callers handle null returns
        return;
      }
      setTimeout(check, 100);
    };
    check();
  });
}

export async function clarityIdentify(
  customId: string,
  customSessionId?: string,
  customPageId?: string,
  friendlyName?: string,
): Promise<{
  id: string;
  session: string;
  page: string;
  userHint: string;
} | null> {
  if (typeof window === "undefined" || !window.clarity) return null;
  try {
    await clarityReady();
    if (!window.clarity) return null;
    const result = await window.clarity(
      "identify",
      customId,
      customSessionId,
      customPageId,
      friendlyName,
    );
    return result ?? null;
  } catch (error) {
    return null;
  }
}

export function claritySet(key: string, value: string | string[]): void {
  if (typeof window === "undefined" || !window.clarity) return;
  try {
    window.clarity("set", key, value);
  } catch (error) {
    // Silent fail
  }
}

export function clarityEvent(name: string): void {
  if (typeof window === "undefined" || !window.clarity) return;
  try {
    window.clarity("event", name);
  } catch (error) {
    // Silent fail
  }
}
